/**
 * Maintenance page.
 * Log maintenance events via modal (openDetailModal pattern).
 * Displays recent maintenance history.
 * Route: #/maintenance
 * Requirement: 7.1, design consistency requirement
 */

import { getUser } from '../services/auth.js';
import { getAircraft, getAllMaintenanceEvents } from '../services/api.js';
import { logMaintenance } from '../services/transactions.js';
import { navigate } from '../services/router.js';
import { MAINTENANCE_TYPES } from '../utils/constants.js';
import { formatDateUK } from '../utils/format.js';
import { openDetailModal } from '../components/detail-modal.js';
import { createDataGrid } from '../components/data-grid.js';

// ── Exported pure helpers for testability ─────────────

/**
 * Build the maintenance type options for the modal select field.
 * @returns {Array<{value: string, label: string}>}
 */
export function getMaintenanceTypeOptions() {
  return [
    { value: MAINTENANCE_TYPES.FIFTY_HR, label: '50-Hour' },
    { value: MAINTENANCE_TYPES.ANNUAL, label: 'Annual' },
    { value: MAINTENANCE_TYPES.OIL_CHANGE, label: 'Oil Change' },
    { value: MAINTENANCE_TYPES.OTHER, label: 'Other' },
  ];
}

/**
 * Format a maintenance type value for display.
 * @param {string} type
 * @returns {string}
 */
export function formatMaintenanceType(type) {
  const map = {
    [MAINTENANCE_TYPES.FIFTY_HR]: '50-Hour',
    [MAINTENANCE_TYPES.ANNUAL]: 'Annual',
    [MAINTENANCE_TYPES.OIL_CHANGE]: 'Oil Change',
    [MAINTENANCE_TYPES.OTHER]: 'Other',
  };
  return map[type] || type || '—';
}

// ── Page state ────────────────────────────────────────

let aircraftList = [];

// ── Main render ───────────────────────────────────────

function buildMaintScheduleTable(aircraft) {
  const today = new Date();

  function cell50hr(ac) {
    if (ac.hours_since_last_50hr == null) return { cls: 'maint--unknown', text: '—' };
    const rem = 50 - Number(ac.hours_since_last_50hr);
    return {
      cls: rem < 0 ? 'maint--overdue' : rem < 10 ? 'maint--amber' : 'maint--ok',
      text: rem < 0 ? `${Math.abs(rem).toFixed(1)} hrs overdue` : `${rem.toFixed(1)} hrs rem.`,
    };
  }

  function cellAnnual(ac) {
    if (!ac.last_annual_date) return { cls: 'maint--unknown', text: '—' };
    const due = new Date(ac.last_annual_date);
    due.setFullYear(due.getFullYear() + 1);
    const days = Math.round((due - today) / 86400000);
    return {
      cls: days < 0 ? 'maint--overdue' : days < 30 ? 'maint--amber' : 'maint--ok',
      text: days < 0 ? `${Math.abs(days)} days overdue` : `Due ${due.toISOString().split('T')[0]}`,
    };
  }

  function cellOil(ac) {
    if (ac.next_oil_change_hours == null || ac.total_hours == null) return { cls: 'maint--unknown', text: '—' };
    const rem = Number(ac.next_oil_change_hours) - Number(ac.total_hours);
    return {
      cls: rem < 0 ? 'maint--overdue' : rem < 5 ? 'maint--amber' : 'maint--ok',
      text: rem < 0 ? `${Math.abs(rem).toFixed(1)} hrs overdue` : `${rem.toFixed(1)} hrs rem.`,
    };
  }

  const rows = aircraft.map((ac) => {
    const svc = ac.status === 'UNSERVICEABLE'
      ? '<span class="badge badge--danger">UNSVC</span>'
      : '<span class="badge badge--success">SVC</span>';
    const h = cell50hr(ac);
    const a = cellAnnual(ac);
    const o = cellOil(ac);
    return `<tr>
      <td><strong>${ac.registration}</strong><br><span class="text-muted">${ac.type || ''}</span></td>
      <td>${svc}</td>
      <td class="${h.cls}">${h.text}</td>
      <td class="${a.cls}">${a.text}</td>
      <td class="${o.cls}">${o.text}</td>
    </tr>`;
  }).join('');

  return `<table class="maint-schedule-table">
    <thead><tr>
      <th>Aircraft</th>
      <th>Serviceable</th>
      <th>50hr Check</th>
      <th>Annual</th>
      <th>Oil Change</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>`;
}

export async function renderMaintenance() {
  const app = document.getElementById('app');
  const user = getUser();

  if (!user) { navigate('/login'); return; }

  app.innerHTML = `
    <div class="container">
      <h2>Maintenance</h2>
      <div id="maint-msg"></div>
      <div id="maint-schedule-section">
        <h3>Aircraft Maintenance Status</h3>
        <div id="maint-schedule" class="maint-schedule-grid"><p>Loading…</p></div>
      </div>
      <div style="margin:1.5rem 0 1rem;">
        <button id="btn-log-maintenance" class="btn btn--primary">Log Maintenance Event</button>
      </div>
      <div class="card">
        <h3>Recent Maintenance Events</h3>
        <div id="maint-history">Loading…</div>
      </div>
    </div>
  `;

  // Load aircraft for schedule + modal dropdown
  try {
    aircraftList = await getAircraft();
    const active = aircraftList.filter(a => !a.archived && !a.meta?.retired && !(a.name || '').includes('[RETIRED]'));
    const scheduleEl = document.getElementById('maint-schedule');
    if (scheduleEl) {
      scheduleEl.innerHTML = active.length
        ? buildMaintScheduleTable(active)
        : '<p class="text-muted">No active aircraft.</p>';
    }
  } catch (err) {
    showMsg('Could not load aircraft: ' + err.message, true);
  }

  // Load maintenance history
  await loadMaintenanceHistory();

  // Log Maintenance button → modal
  const logBtn = document.getElementById('btn-log-maintenance');
  if (logBtn) {
    logBtn.addEventListener('click', () => openLogMaintenanceModal());
  }
}

// ── Log Maintenance modal ─────────────────────────────

function getAircraftOptions() {
  return aircraftList
    .filter((ac) => {
      if (ac.meta && ac.meta.retired) return false;
      if (ac.name && ac.name.includes('[RETIRED]')) return false;
      return true;
    })
    .map((ac) => ({ value: ac.id, label: `${ac.registration} (${ac.type})` }));
}

function openLogMaintenanceModal() {
  const fields = [
    { name: 'aircraft_id', label: 'Aircraft', type: 'select', options: getAircraftOptions(), required: true },
    { name: 'type', label: 'Type', type: 'select', options: getMaintenanceTypeOptions(), required: true },
    { name: 'performed_date', label: 'Performed Date', type: 'date', required: true },
    { name: 'description', label: 'Description', type: 'textarea', required: false, placeholder: 'Maintenance details…' },
  ];

  openDetailModal({
    title: 'Log Maintenance',
    fields,
    onSave: async (formData) => {
      const result = await logMaintenance({
        aircraft_id: formData.aircraft_id,
        type: formData.type,
        performed_date: formData.performed_date,
        description: formData.description || undefined,
      });

      if (result.error) {
        throw new Error(result.error);
      }

      showMsg('Maintenance event logged successfully!', false);
      await loadMaintenanceHistory();
    },
  });
}

// ── Load and render maintenance history ───────────────

async function loadMaintenanceHistory() {
  const historyEl = document.getElementById('maint-history');
  if (!historyEl) return;

  try {
    const events = await getAllMaintenanceEvents();

    if (!events || !events.length) {
      historyEl.innerHTML = '<p style="color:var(--color-text-muted);">No maintenance events recorded.</p>';
      return;
    }

    historyEl.innerHTML = '';

    const columns = [
      {
        key: 'aircraft_reg',
        label: 'Aircraft',
        html: true,
        format: (_, row) => {
          const reg = row.aircraft?.registration;
          const id = row.aircraft?.id || row.aircraft_id;
          if (!reg) return '—';
          if (!id) return reg;
          return `<a href="#/aircraft/${id}" class="aircraft-link">${reg}</a>`;
        },
      },
      {
        key: 'type',
        label: 'Type',
        format: (v) => formatMaintenanceType(v),
      },
      {
        key: 'performed_at',
        label: 'Date',
        format: (v) => v ? formatDateUK(v) : '—',
      },
      {
        key: 'description',
        label: 'Description',
        format: (v) => {
          if (!v) return '—';
          return v.length > 80 ? v.slice(0, 80) + '…' : v;
        },
      },
    ];

    const grid = createDataGrid({
      columns,
      data: events,
      defaultSort: 'performed_at',
      defaultOrder: 'desc',
      emptyMessage: 'No maintenance events',
      filterable: true,
    });

    historyEl.appendChild(grid);
  } catch (err) {
    historyEl.innerHTML = `<p style="color:var(--color-danger);">Error loading maintenance history: ${err.message}</p>`;
  }
}

// ── UI helpers ────────────────────────────────────────

function showMsg(text, isError) {
  const el = document.getElementById('maint-msg');
  if (!el) return;
  el.textContent = text;
  el.style.color = isError ? 'var(--color-danger)' : 'var(--color-success)';
  el.style.padding = '0.75rem';
  el.style.marginBottom = '1rem';
  if (!isError) {
    setTimeout(() => {
      el.textContent = '';
      el.style.padding = '';
      el.style.marginBottom = '';
    }, 5000);
  }
}
